import { IamSdk } from 'lib-klab-sdks';
import { PageMeta } from '../../../domain/models/organization/page.model';
import {
    AssignedPermissionDomain,
    PermissionModuleTree,
    PermissionTree,
    RoleDetail,
    RoleScope,
    RoleSummary,
} from '../../../domain/models/session/roles.model';
import { PermissionType } from '../../../domain/models/session/session';

export function mapSdkPaginationMetaToDomain(
    p?: IamSdk.PaginationMeta
): PageMeta {
    return {
        number: p?.page ?? 0,
        size: p?.size ?? 0,
        totalElements: p?.totalElements ?? 0,
        totalPages: p?.totalPages ?? 0,
    };
}

function mapSdkLocationToDomain(loc: IamSdk.LocationResponse | any) {
    return {
        id: Number(loc?.locationId ?? 0),
        name: String(loc?.name ?? ''),
    };
}

function mapSdkDepartmentToDomain(dep: IamSdk.DepartmentResponse | any) {
    return {
        id: Number(dep?.departmentId ?? 0),
        name: String(dep?.name ?? ''),
    };
}

export function mapSdkRoleSummaryToDomain(
    sdk: IamSdk.RoleSummary
): RoleSummary {
    return {
        id: Number(sdk.roleId ?? 0),
        name: String(sdk.name ?? ''),
        isEnabled: Boolean(sdk.isEnabled),
        allowRead: Boolean(sdk.allowRead),
        allowCreate: Boolean(sdk.allowCreate),
        allowUpdate: Boolean(sdk.allowUpdate),
        allowDelete: Boolean(sdk.allowDelete),
        locations: (sdk.locations ?? []).map(mapSdkLocationToDomain),
        departments: (sdk.departments ?? []).map(mapSdkDepartmentToDomain),
        createdAt: String(sdk.createdAt ?? ''),
    };
}

function mapSdkAssignedPermissionToDomain(
    p: IamSdk.AssignedPermission | any
): AssignedPermissionDomain {
    return {
        permissionId: Number(p?.permissionId ?? 0),
        code: String(p?.code ?? ''),
        name: String(p?.name ?? ''),
        permissionType: (p?.permissionType as PermissionType) ?? PermissionType.Action,
        isEnabled: Boolean(p?.isEnabled),
        children: Array.isArray(p?.children)
            ? (p.children as any[]).map(mapSdkAssignedPermissionToDomain)
            : null,
    };
}

function mapSdkRoleScopeToDomain(
    s: IamSdk.CreateRoleScopeRequest | any
): RoleScope {
    return {
        locationId: s?.locationId ?? undefined,
        departmentId: s?.departmentId ?? undefined,
    };
}

export function mapSdkRoleDetailToDomain(
    sdk: IamSdk.RoleDetailResponse
): RoleDetail {
    const base = mapSdkRoleSummaryToDomain(sdk as any);
    return {
        ...base,
        permissions: (sdk.permissions ?? []).map(mapSdkAssignedPermissionToDomain),
        scopes: (sdk.scopes ?? []).map(mapSdkRoleScopeToDomain),
        updatedAt: sdk.updatedAt ?? undefined,
    };
}

export function mapSdkPermissionModuleToDomain(
    m: IamSdk.PermissionModule | any
): PermissionModuleTree {
    const childrenRaw = m?.children;
    const children: PermissionModuleTree[] = Array.isArray(childrenRaw)
        ? childrenRaw
            .filter((c) => typeof c === 'object' && c !== null)
            .map(mapSdkPermissionModuleToDomain)
        : [];

    return {
        permissionId: Number(m?.permission_id ?? 0),
        code: String(m?.code ?? ''),
        name: String(m?.name ?? ''),
        description: m?.description ?? undefined,
        permissionType:
            (m?.permission_type as PermissionType) ?? PermissionType.MenuItem,
        isEnabled: m?.is_enabled ?? undefined,
        icon: m?.icon ?? undefined,
        order: Number(m?.order ?? 0),
        route: m?.route ?? undefined,
        children,
    };
}

export function mapSdkPermissionTreeToDomain(
    resp: IamSdk.PermissionTreeResponse
): PermissionTree {
    return {
        modules: (resp.modules ?? []).map(mapSdkPermissionModuleToDomain),
    };
}